// frontend/src/pages/OsNsaPage.js
import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';
import { 
    PlusCircle, Search, Edit, Trash2, X, FileText, Printer, 
    User, MapPin, AlertTriangle, Loader2, ArrowLeft, ArrowRight,
    Eye // Ícone para visualização (Cliente)
} from 'lucide-react';

// --- IMPORTAÇÃO DOS MODAIS ---
import OsNsaFormModal from '../components/modals/OsNsaFormModal';
import NovoRecebimentoModal from '../components/modals/NovoRecebimentoModal'; 

import html2canvas from 'html2canvas';
import { jsPDF } from "jspdf"; // Certifique-se de ter instalado: npm install jspdf jspdf-autotable
import 'jspdf-autotable';

// --- Hook useDebounce ---
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

// --- Helper de Formatação de Data ---
const formatDateToBR = (dateString) => {
    if (!dateString) return '-';
    const cleanDate = dateString.toString().split('T')[0];
    const parts = cleanDate.split('-'); 
    if (parts.length !== 3) return cleanDate;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

// --- Componente: Modal de Confirmação de Exclusão (RESPONSIVO) ---
const ConfirmDeleteModal = ({ onClose, onConfirm, loading, error }) => {
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black bg-opacity-50 px-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between p-4 border-b">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-full">
                            <AlertTriangle className="text-red-500" size={24} />
                        </div>
                        <h2 className="text-lg md:text-xl font-bold text-gray-800">Excluir OS/NSA</h2>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20} /></button>
                </div>
                <div className="p-6">
                    {error ? (
                        <p className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</p>
                    ) : (
                        <p className="text-gray-600 text-sm md:text-base">Tem certeza de que deseja excluir esta Ordem de Serviço? Esta ação não pode ser desfeita.</p>
                    )}
                </div>
                <div className="flex flex-col-reverse md:flex-row justify-end gap-2 p-4 bg-gray-50 border-t rounded-b-lg">
                    <button type="button" onClick={onClose} disabled={loading} className="w-full md:w-auto px-4 py-2 font-semibold bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                    {!error && (
                        <button 
                            onClick={onConfirm}
                            disabled={loading} 
                            className="w-full md:w-auto flex items-center justify-center px-4 py-2 font-semibold text-white bg-red-600 rounded-md disabled:bg-red-300 hover:bg-red-700"
                        >
                            {loading ? <Loader2 className="animate-spin" /> : 'Sim, Excluir'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Componente Principal: OsNsaPage ---
const OsNsaPage = ({ formatCurrency, onDataChange, currentUser, dataChangeKey }) => {
    const { apiFetch, permissions } = useApi(); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [osList, setOsList] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    // Estados para Exclusão
    const [deletingOsId, setDeletingOsId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    // Estados para Modal de Formulário da OS
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingOs, setEditingOs] = useState(null);

    // --- NOVOS ESTADOS PARA INTEGRAÇÃO FINANCEIRA ---
    const [isRecebimentoOpen, setIsRecebimentoOpen] = useState(false);
    const [recebimentoData, setRecebimentoData] = useState(null);

    // Estado para PDF
    const [pdfLoadingId, setPdfLoadingId] = useState(null);

    // Permissões e Flags
    const canCreate = permissions?.comercial_os_create_btn || currentUser?.is_super_user;
    const canEdit = permissions?.comercial_os_edit || currentUser?.is_super_user;
    const canDelete = permissions?.comercial_os_delete || currentUser?.is_super_user;
    const hideSensitiveData = permissions?.comercial_os_hide_sensitive && !currentUser?.is_super_user;
    const canPrintOs = permissions?.comercial_os_print_os || currentUser?.is_super_user; // <-- NOVA TRAVA AQUI
    
    // --- IMPORTANTE: Flag de Cliente ---
    const isClient = currentUser?.is_client;

    // Função para buscar dados
    const fetchData = useCallback(async (page = 1, search = '') => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                page: page,
                limit: 15,
                search: search,
            });
            const url = `/os-nsa?${params.toString()}`;
            const data = await apiFetch(url);
            
            setOsList(data.osnsas || []);
            setTotalPages(data.totalPages || 1);
        } catch (err) {
            console.error("Erro ao buscar OS/NSA:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    // Efeito inicial e de busca
    useEffect(() => {
        fetchData(currentPage, debouncedSearchQuery);
    }, [currentPage, debouncedSearchQuery, fetchData]);

    // Efeito para resetar página na busca
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchQuery]);

    // --- EFEITO NOVO: Atualização em Tempo Real ---
    useEffect(() => {
        if (dataChangeKey > 0) {
            fetchData(currentPage, debouncedSearchQuery);
        }
    }, [dataChangeKey, fetchData, currentPage, debouncedSearchQuery]);

    // --- Handlers de Formulário OS ---
    const handleOpenNewOs = () => {
        setEditingOs(null);
        setIsModalOpen(true);
    };

    const handleEditClick = (os) => {
        setEditingOs(os);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingOs(null);
    };

    const handleModalSave = () => {
        handleModalClose();
        fetchData(currentPage, debouncedSearchQuery);
        if (onDataChange) onDataChange();
    };

    // --- LÓGICA PONTE: DO MODAL OS PARA O MODAL FINANCEIRO ---
    const handleFaturarBridge = (dataFromOs) => {
        const dataToFinance = {
            cliente_id: dataFromOs.cliente_id,
            cliente_nome: dataFromOs.cliente_nome,
            os_number: dataFromOs.os_number,
            valor_total: dataFromOs.valor_total
        };

        setRecebimentoData(dataToFinance);
        setIsModalOpen(false);
        setIsRecebimentoOpen(true);
    };

    const handleRecebimentoSave = () => {
        setIsRecebimentoOpen(false);
        setRecebimentoData(null);
        
        if (onDataChange) onDataChange();
        fetchData(currentPage, debouncedSearchQuery);
        
        alert("Fatura gerada e vinculada com sucesso!");
    };

    // --- Handlers de Exclusão ---
    const handleDeleteClick = (os) => {
        if (os.faturada === 1) {
            alert("Esta OS está faturada e não pode ser excluída. Exclua o recebimento no módulo financeiro para liberar.");
            return;
        }

        setDeleteError('');
        setDeletingOsId(os.id);
    };

    const handleConfirmDelete = async () => {
        if (!deletingOsId) return;
        setDeleteLoading(true);
        setDeleteError('');
        try {
            await apiFetch(`/os-nsa/${deletingOsId}`, { method: 'DELETE' });
            setDeletingOsId(null);
            fetchData(currentPage, debouncedSearchQuery);
            if (onDataChange) onDataChange();
        } catch (err) {
            console.error("Erro ao excluir OS:", err);
            setDeleteError(err.message);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    // --- Helper: Renderizar HTML para Imagem (PDF) ---
    const renderHtmlToImage = async (htmlContent, targetWidthInPdfUnits) => {
        if (!htmlContent || htmlContent === '<p><br></p>') return null;
        const container = document.createElement('div');
        container.innerHTML = htmlContent;
        const widthInPixels = targetWidthInPdfUnits * 3.78 * 2; 
        container.style.width = `${widthInPixels}px`;
        container.style.padding = '10px'; 
        container.style.margin = '0';
        container.style.backgroundColor = '#ffffff'; 
        container.style.fontSize = '24px'; 
        container.style.fontFamily = 'Helvetica, Arial, sans-serif'; 
        container.style.lineHeight = '1.4';
        container.style.color = '#000000';
        container.style.position = 'absolute';
        container.style.top = '-9999px';
        container.style.left = '-9999px';
        
        const styleTag = document.createElement('style');
        styleTag.innerHTML = `
            * { box-sizing: border-box; }
            body { margin: 0; padding: 0; }
            p { margin: 0 0 5px 0; padding: 0; }
            ul { margin: 5px 0 10px 0; padding-left: 20px; list-style-type: disc; list-style-position: inside; }
            ol { margin: 5px 0 10px 0; padding-left: 20px; list-style-type: decimal; list-style-position: inside; }
            li { margin-bottom: 2px; }
            .ql-align-center { text-align: center; }
            .ql-align-right { text-align: right; }
            .ql-align-justify { text-align: justify; }
            strong { font-weight: bold; }
            em { font-style: italic; }
            u { text-decoration: underline; }
        `;
        container.appendChild(styleTag);
        document.body.appendChild(container);
        try {
            const canvas = await html2canvas(container, { scale: 1, backgroundColor: '#ffffff', logging: false, useCORS: true });
            document.body.removeChild(container);
            const imgData = canvas.toDataURL('image/png');
            const ratio = canvas.height / canvas.width;
            const pdfHeight = targetWidthInPdfUnits * ratio;
            return { imgData, height: pdfHeight };
        } catch (e) {
            console.error("Erro ao renderizar HTML para PDF:", e);
            if (container.parentNode) document.body.removeChild(container);
            return null;
        }
    };

    // --- Helper: Carregar e Converter Imagem via Canvas ---
    const getImageDataUrlFromProxy = async (originalUrl, apiFetch) => {
        return new Promise(async (resolve, reject) => {
            try {
                const data = await apiFetch(`/os-nsa-external/proxy-image?url=${encodeURIComponent(originalUrl)}`);
                const rawBase64 = data.image; 

                if (rawBase64.startsWith('data:text/html')) {
                    console.warn(`[Proxy Error] HTML recebido: ${originalUrl}`);
                    return reject(new Error("Imagem inválida."));
                }

                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.85); 
                    resolve(jpegDataUrl);
                };
                img.onerror = (e) => {
                    console.error("Erro img.onerror:", e);
                    reject(new Error("Falha ao processar imagem."));
                };
                img.src = rawBase64;

            } catch (err) {
                reject(err);
            }
        });
    };

    // --- Lógica de Geração de PDF ---
    const handleGeneratePDF = async (osId) => {
        setPdfLoadingId(osId);
        try {
            const data = await apiFetch(`/os-nsa/${osId}/pdf-data`);
            const { os, entity, settings } = data;
            
            // BUSCA OS LAUDOS TÉCNICOS PARA PEGAR AS FOTOS
            let reports = [];
            try {
                reports = await apiFetch(`/os-nsa/${osId}/technical-reports`);
            } catch (e) {
                console.warn("Erro ao buscar laudos para impressão de fotos:", e);
            }

            // const { jsPDF } = window.jspdf; // Usando importação direta
            if (!jsPDF) throw new Error("Biblioteca PDF não carregada.");
            
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 14;
            let cursorY = 20;

            doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(50, 50, 50);
            doc.text(`OS/NSA: ${os.os_number}`, pageWidth - margin, cursorY, { align: 'right' });
            doc.setFontSize(10); doc.setFont("helvetica", "normal");
            doc.text(`Data: ${formatDateToBR(os.data_criacao)}`, pageWidth - margin, cursorY + 7, { align: 'right' });
            cursorY += 20;

            if (entity.logo && entity.logo.data) {
                try {
                    const base64String = btoa(new Uint8Array(entity.logo.data).reduce((data, byte) => data + String.fromCharCode(byte), ''));
                    doc.addImage(`data:image/png;base64,${base64String}`, 'PNG', margin, cursorY - 5, 25, 25);
                } catch (e) { console.warn("Erro logo PDF:", e); }
            }

            const entityTextX = margin + 30; 
            doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 0, 0);
            doc.text(entity.name || 'Minha Empresa', entityTextX, cursorY);
            doc.setFontSize(9); doc.setFont("helvetica", "normal");
            doc.text(`CNPJ: ${entity.cnpj || ''}  |  I.E: ${entity.state_registration || 'Isento'}`, entityTextX, cursorY + 6);
            doc.text(`${entity.address_street || ''}, ${entity.address_number || ''}`, entityTextX, cursorY + 11);
            doc.text(`${entity.address_city || ''} - ${entity.address_state || ''}`, entityTextX, cursorY + 16);
            cursorY += 35;

            doc.setFillColor(240, 240, 240); doc.rect(margin, cursorY, pageWidth - (margin * 2), 8, 'F');
            doc.setFontSize(11); doc.setFont("helvetica", "bold");
            const pdfTitle = hideSensitiveData ? "SERVIÇOS SOLICITADOS" : "ORÇAMENTO DE SERVIÇOS SOLICITADOS";
            doc.text(pdfTitle, pageWidth / 2, cursorY + 5.5, { align: 'center' });
            cursorY += 15;

            doc.setFontSize(10); doc.setFont("helvetica", "bold");
            doc.text(`Solicitante: ${os.cliente_nome}`, margin, cursorY);
            doc.setFont("helvetica", "normal");
            const contactDisplay = hideSensitiveData ? '******' : (os.contato_nome || '');
            const emailDisplay = hideSensitiveData ? '******' : (os.contato_email || '');
            doc.text(`Contato: ${contactDisplay}`, margin, cursorY + 6);
            doc.text(`Email: ${emailDisplay}`, pageWidth / 2, cursorY + 6);
            doc.text(`Endereço: ${os.cliente_endereco || ''}`, margin, cursorY + 12);
            cursorY += 20;

            doc.setFont("helvetica", "bold");
            doc.text(`Data Prevista: ${formatDateToBR(os.data_prevista) || 'À Combinar'}`, margin, cursorY);
            doc.text(`Prazo (Dias): ${os.dias_conclusao || '-'}`, pageWidth / 2 - 20, cursorY);
            doc.text(`Deslocamento: ${os.deslocamento_km || '0'} km`, pageWidth - margin, cursorY, { align: 'right' });
            cursorY += 10;

            doc.autoTable({
                startY: cursorY,
                head: [['Descrição', 'Qtde', 'Vl. Unit.', 'Total']],
                body: os.servicos.map(s => [
                    s.descricao, s.quantidade,
                    hideSensitiveData ? '******' : formatCurrency(s.valor_unitario, 'BRL'),
                    hideSensitiveData ? '******' : formatCurrency(s.valor_total, 'BRL')
                ]),
                theme: 'grid',
                headStyles: { fillColor: [66, 66, 66] },
                columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 20, halign: 'center' }, 2: { cellWidth: 30, halign: 'right' }, 3: { cellWidth: 35, halign: 'right', fontStyle: 'bold' } },
                foot: [['', '', 'TOTAL:', hideSensitiveData ? '******' : formatCurrency(os.valor_total, 'BRL')]],
                footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'right' }
            });
            cursorY = doc.lastAutoTable.finalY + 15;

            if (!hideSensitiveData) {
                const addHtmlSection = async (title, htmlContent) => {
                    if (!htmlContent) return;
                    if (cursorY > 250) { doc.addPage(); cursorY = 20; }
                    doc.setFont("helvetica", "bold"); doc.setFontSize(9);
                    doc.text(title, margin, cursorY); cursorY += 5;
                    const maxWidth = pageWidth - (margin * 2);
                    const cleanHtml = htmlContent.replace(/<p><br><\/p>/g, '<br>'); 
                    const rendered = await renderHtmlToImage(cleanHtml, maxWidth);
                    if (rendered) {
                        if (cursorY + rendered.height > 280) { doc.addPage(); cursorY = 20; }
                        doc.addImage(rendered.imgData, 'PNG', margin, cursorY, maxWidth, rendered.height);
                        cursorY += rendered.height + 10;
                    } else { cursorY += 5; }
                };
                await addHtmlSection("Condições de Pagamento:", settings.os_layout_condicoes);
                await addHtmlSection("Validade da Proposta:", settings.os_layout_validade);
                await addHtmlSection("Observações:", settings.os_layout_obs);
                
                if (cursorY > 240) { doc.addPage(); cursorY = 20; }
                const sigY = cursorY + 10;
                
                if (settings.os_layout_assinatura1_path) {
                    try {
                        const imgUrl = `/api/uploads/layout_images/${settings.os_layout_assinatura1_path}`;
                        const getBase64 = async (url) => {
                            const response = await fetch(url, { headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }});
                            const blob = await response.blob();
                            return new Promise((resolve) => { const reader = new FileReader(); reader.onloadend = () => resolve(reader.result); reader.readAsDataURL(blob); });
                        };
                        const base64Sig1 = await getBase64(imgUrl);
                        const imgX = (margin + 40) - 17.5;
                        doc.addImage(base64Sig1, 'PNG', imgX, sigY, 35, 15);
                    } catch (e) {}
                }
                doc.line(margin + 10, sigY + 17, margin + 70, sigY + 17); 
                if (settings.os_layout_frase1) {
                    const frase1Img = await renderHtmlToImage(settings.os_layout_frase1, 60); 
                    if (frase1Img) doc.addImage(frase1Img.imgData, 'PNG', margin + 10, sigY + 19, 60, frase1Img.height);
                } else { doc.setFontSize(8); doc.text('CONTRATANTE', margin + 40, sigY + 22, { align: 'center' }); }

                if (settings.os_layout_assinatura2_path) {
                    try {
                        const imgUrl = `/api/uploads/layout_images/${settings.os_layout_assinatura2_path}`;
                        const getBase64 = async (url) => {
                            const response = await fetch(url, { headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }});
                            const blob = await response.blob();
                            return new Promise((resolve) => { const reader = new FileReader(); reader.onloadend = () => resolve(reader.result); reader.readAsDataURL(blob); });
                        };
                        const base64Sig2 = await getBase64(imgUrl);
                        const imgX = (pageWidth - margin - 40) - 17.5;
                        doc.addImage(base64Sig2, 'PNG', imgX, sigY, 35, 15);
                    } catch (e) {}
                }
                doc.line(pageWidth - margin - 70, sigY + 17, pageWidth - margin - 10, sigY + 17); 
                if (settings.os_layout_frase2) {
                    const frase2Img = await renderHtmlToImage(settings.os_layout_frase2, 60);
                    if (frase2Img) doc.addImage(frase2Img.imgData, 'PNG', pageWidth - margin - 70, sigY + 19, 60, frase2Img.height);
                } else { doc.text('CONTRATADA', pageWidth - margin - 40, sigY + 22, { align: 'center' }); }
                
                if (settings.os_layout_footer) {
                    const footerWidth = pageWidth - (margin * 2);
                    const footerImg = await renderHtmlToImage(settings.os_layout_footer, footerWidth);
                    if (footerImg) { const footerY = 280 - footerImg.height; doc.addImage(footerImg.imgData, 'PNG', margin, footerY, footerWidth, footerImg.height); }
                }
            }

            // --- IMPRESSÃO DE FOTOS GERAIS DA OS (LEGADO) ---
            let osGeneralPhotos = [];
            try {
                if (os.photos) {
                    const parsed = typeof os.photos === 'string' ? JSON.parse(os.photos) : os.photos;
                    if (Array.isArray(parsed)) osGeneralPhotos = parsed.filter(p => p && p.trim() !== '');
                }
            } catch (e) { console.error("Erro ao ler fotos gerais", e); }

            if (osGeneralPhotos.length > 0) {
                doc.addPage();
                doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 0, 0);
                doc.text(`FOTOS GERAIS - OS: ${os.os_number}`, pageWidth / 2, 20, { align: 'center' });
                doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.5);
                doc.line(margin, 22, pageWidth - margin, 22);

                const boxWidth = 88;
                const boxHeight = 55; // AJUSTADO
                const startX = 12;
                const startY = 30;
                const gapX = 8;
                const gapY = 5; // AJUSTADO

                for (let i = 0; i < Math.min(osGeneralPhotos.length, 8); i++) {
                    const col = i % 2; 
                    const row = Math.floor(i / 2); 
                    const xPos = startX + (col * (boxWidth + gapX));
                    const yPos = startY + (row * (boxHeight + gapY));

                    doc.setFontSize(9); doc.setTextColor(50, 50, 50);
                    doc.text(`Foto ${i + 1}`, xPos, yPos - 2);

                    doc.setDrawColor(200, 200, 200);
                    doc.rect(xPos, yPos, boxWidth, boxHeight);

                    const photoUrl = osGeneralPhotos[i]; 
                    try {
                        const imgData = await getImageDataUrlFromProxy(photoUrl, apiFetch);
                        doc.addImage(imgData, 'JPEG', xPos + 1, yPos + 1, boxWidth - 2, boxHeight - 2, undefined, 'FAST');
                    } catch (err) {
                        doc.setFontSize(8); doc.setTextColor(150, 150, 150);
                        doc.text("Imagem indisponível", xPos + (boxWidth/2), yPos + (boxHeight/2), { align: 'center' });
                    }
                }
            }

            // --- NOVA LÓGICA: IMPRESSÃO DE FOTOS DOS LAUDOS TÉCNICOS ---
            for (const report of reports) {
                let reportPhotos = [];
                if (report.data && report.data.photos) {
                    const raw = report.data.photos;
                    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    if (Array.isArray(parsed)) {
                        reportPhotos = parsed.filter(p => p && p.trim() !== '');
                    }
                }

                if (reportPhotos.length > 0) {
                    doc.addPage();
                    
                    const boxWidth = 88;
                    const boxHeight = 55; // AJUSTADO
                    const startX = 12;
                    const startY = 35; 
                    const gapX = 8;
                    const gapY = 5; // AJUSTADO

                    // TÍTULO
                    doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 0, 0);
                    const refText = os.os_number || report.data?.referencia || 'N/A';
                    doc.text(`LAUDO FOTOGRÁFICO - OS: ${refText}`, pageWidth / 2, 20, { align: 'center' });

                    // SUBTÍTULO (PLACA, CHASSI, SÉRIE)
                    const placa = report.data?.equipamento_placa;
                    const chassi = report.data?.equipamento_serie;
                    const serie = report.data?.lanca_serie;

                    if (placa || chassi || serie) {
                        doc.setFontSize(11);
                        let subtitle = "";
                        if (placa) subtitle += `PLACA: ${placa}   `;
                        if (chassi) subtitle += `CHASSI: ${chassi}   `;
                        if (serie) subtitle += `SÉRIE: ${serie}`;
                        doc.text(subtitle.trim(), pageWidth / 2, 27, { align: 'center' });
                    }

                    doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.5);
                    doc.line(margin, 30, pageWidth - margin, 30);

                    // LOOP DE FOTOS (ATÉ 8)
                    for (let i = 0; i < Math.min(reportPhotos.length, 8); i++) {
                        const col = i % 2;
                        const row = Math.floor(i / 2);
                        const xPos = startX + (col * (boxWidth + gapX));
                        const yPos = startY + (row * (boxHeight + gapY));

                        // Rótulo
                        doc.setFontSize(9); doc.setTextColor(50, 50, 50);
                        doc.setFont("helvetica", "bold");
                        doc.text(`Foto ${i + 1}`, xPos, yPos - 2);

                        // Borda
                        doc.setDrawColor(200, 200, 200);
                        doc.rect(xPos, yPos, boxWidth, boxHeight);

                        // Imagem
                        const photoUrl = reportPhotos[i];
                        try {
                            const imgData = await getImageDataUrlFromProxy(photoUrl, apiFetch);
                            doc.addImage(imgData, 'JPEG', xPos + 1, yPos + 1, boxWidth - 2, boxHeight - 2, undefined, 'FAST');
                        } catch (err) {
                            doc.setFontSize(8); doc.setTextColor(150, 150, 150);
                            doc.text("Imagem indisponível", xPos + (boxWidth/2), yPos + (boxHeight/2), { align: 'center' });
                        }
                    }
                }
            }

            doc.save(`OS_${os.os_number}.pdf`);
        } catch (err) {
            console.error("Erro ao gerar PDF:", err);
            alert("Erro ao gerar PDF: " + err.message);
        } finally {
            setPdfLoadingId(null);
        }
    };

    return (
        // AJUSTE MOBILE: Padding reduzido no mobile
        <div className="p-2 md:p-6">
            <div className="flex flex-col md:flex-row justify-between md:items-center mb-4 md:mb-6 gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                        {isClient ? 'Minhas Ordens de Serviço' : 'Ordens de Serviço'}
                    </h1>
                    <p className="text-sm md:text-base text-gray-600">
                        {isClient ? 'Visualize e imprima seus laudos técnicos.' : 'Gerencie seus orçamentos e serviços.'}
                    </p>
                </div>
                
                {canCreate && !isClient && (
                    <button
                        onClick={handleOpenNewOs}
                        // AJUSTE MOBILE: Botão largura total no celular
                        className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 active:scale-95 transition-transform"
                    >
                        <PlusCircle size={18} />
                        <span>Novo OS/NSA</span>
                    </button>
                )}
            </div>

            <div className="mb-4 p-3 md:p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="relative">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        // ATUALIZAÇÃO NO PLACEHOLDER CONFORME SOLICITADO
                        placeholder="Pesquise por OS, Cliente, Placa, Chassi, Série, Lote ou Data..."
                        className="w-full p-2 pl-10 border rounded-md text-sm md:text-base"
                    />
                    <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
                </div>
            </div>

            {loading && <div className="flex justify-center items-center p-8"><Loader2 className="animate-spin text-blue-600" /></div>}
            {error && <p className="text-red-600 p-4 bg-red-100 rounded-md border border-red-200">{error}</p>}

            {!loading && !error && (
                <div className="space-y-3">
                    {osList.length === 0 ? (
                        <p className="p-8 text-center bg-white rounded-lg shadow-sm border border-dashed border-gray-300 text-gray-500">
                            {searchQuery ? 'Nenhum resultado encontrado para a pesquisa.' : 'Nenhuma OS encontrada.'}
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 gap-3 md:gap-4">
                            {osList.map(os => (
                                <div key={os.id} className="bg-white p-3 md:p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                                        
                                        {/* Informações da OS */}
                                        <div className="flex-1 w-full">
                                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                                <span className={`px-2 py-0.5 text-xs font-bold rounded-full flex items-center gap-1 ${os.faturada ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    <FileText size={10} />
                                                    OS: {os.os_number} {os.faturada ? '(Faturada)' : ''}
                                                </span>
                                                
                                                {/* NOVO: STATUS DA OS NA LISTAGEM */}
                                                {!isClient && (
                                                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase border
                                                        ${os.os_status === 'Aprovada' ? 'bg-green-50 text-green-600 border-green-200' : 
                                                          os.os_status === 'Recusada' ? 'bg-red-50 text-red-700 border-red-300' : 
                                                          'bg-yellow-50 text-yellow-700 border-yellow-300'}
                                                    `}>
                                                        {os.os_status || 'Em análise'}
                                                    </span>
                                                )}

                                                <span className="text-xs text-gray-400">
                                                    {formatDateToBR(os.data_criacao)}
                                                </span>
                                            </div>
                                            <h3 className="text-base md:text-lg font-bold text-gray-800 leading-tight">{os.cliente_nome}</h3>
                                            
                                            <div className="mt-2 text-xs md:text-sm text-gray-600 space-y-1">
                                                {os.cliente_endereco && (
                                                    <div className="flex items-start gap-1">
                                                        <MapPin size={14} className="mt-0.5 flex-shrink-0" /> <span className="line-clamp-1">{os.cliente_endereco}</span>
                                                    </div>
                                                )}
                                                {os.contato_nome && (
                                                    <div className="flex items-center gap-1">
                                                        <User size={14} /> Contato: {hideSensitiveData ? '******' : os.contato_nome}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Valores e Botões */}
                                        <div className="flex flex-row md:flex-col items-center md:items-end justify-between w-full md:w-auto gap-3 md:gap-2 mt-2 md:mt-0 pt-3 md:pt-0 border-t md:border-t-0 border-gray-100">
                                            <div className="text-left md:text-right">
                                                {!isClient && (
                                                    <>
                                                        <span className="text-[10px] text-gray-400 uppercase font-bold block">Valor Total</span>
                                                        <span className="text-lg md:text-xl font-bold text-gray-800">
                                                            {hideSensitiveData ? 
                                                                <span className="text-gray-400 tracking-widest">------</span> 
                                                                : formatCurrency(os.valor_total, 'BRL')
                                                            }
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            
                                            <div className="flex items-center gap-2">
                                                {(canEdit || isClient) && (
                                                    <button 
                                                        onClick={() => handleEditClick(os)}
                                                        className="p-2 text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
                                                        title={isClient ? "Visualizar OS e Laudos" : "Editar"}
                                                    >
                                                        {isClient ? <Eye size={18} /> : <Edit size={18} />}
                                                    </button>
                                                )}
                                                {canPrintOs && !isClient && (
                                                    <button 
                                                        onClick={() => handleGeneratePDF(os.id)}
                                                        disabled={pdfLoadingId === os.id}
                                                        className="p-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 disabled:opacity-50 transition-colors"
                                                        title="Gerar PDF"
                                                    >
                                                        {pdfLoadingId === os.id ? <Loader2 size={18} className="animate-spin" /> : <Printer size={18} />}
                                                    </button>
                                                )}
                                                {canDelete && !isClient && (
                                                    <button 
                                                        onClick={() => handleDeleteClick(os)}
                                                        className={`p-2 rounded-lg border transition-colors ${os.faturada ? 'text-gray-300 bg-gray-50 border-gray-100 cursor-not-allowed' : 'text-red-600 bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300'}`}
                                                        title={os.faturada ? "OS Faturada (Bloqueada)" : "Excluir"}
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-3 mt-6 pb-20 md:pb-0">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || loading}
                                className="flex items-center gap-1 px-3 py-2 text-xs md:text-sm font-semibold bg-white border rounded-md shadow-sm disabled:opacity-50"
                            >
                                <ArrowLeft size={14} /> Ant
                            </button>
                            <span className="text-xs md:text-sm text-gray-700 font-medium">
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= totalPages || loading}
                                className="flex items-center gap-1 px-3 py-2 text-xs md:text-sm font-semibold bg-white border rounded-md shadow-sm disabled:opacity-50"
                            >
                                Prox <ArrowRight size={14} />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {isModalOpen && (
                <OsNsaFormModal
                    osToEdit={editingOs}
                    onClose={handleModalClose}
                    onSave={handleModalSave}
                    currentUser={currentUser}
                    formatCurrency={formatCurrency}
                    hideSensitiveData={hideSensitiveData}
                    onFaturar={handleFaturarBridge}
                />
            )}
            
            {isRecebimentoOpen && (
                <NovoRecebimentoModal
                    onClose={() => setIsRecebimentoOpen(false)}
                    onSave={handleRecebimentoSave}
                    formatCurrency={formatCurrency}
                    initialData={recebimentoData}
                />
            )}

            {deletingOsId && (
                <ConfirmDeleteModal
                    onClose={() => setDeletingOsId(null)}
                    onConfirm={handleConfirmDelete}
                    loading={deleteLoading}
                    error={deleteError}
                />
            )}
        </div>
    );
};

export default OsNsaPage;